/**
Copyright (C) 2019-2025, Infraclear Inc. All Rights Reserved.
*/

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Client } from './types';
import { fetchClients, deactivateClient } from './clientService';
import ClientFormDialog from './ClientFormDialog';
import ConfirmDialog from './ConfirmDialog';
import { SnackBarMessage } from '../../../components/snackBar/snackBar';
import './clients.scss';

const tierColorMap: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'info'> = {
  BASIC: 'default',
  PRO: 'primary',
  ENTERPRISE: 'secondary',
};

const statusColorMap: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  ACTIVE: 'success',
  SUSPENDED: 'warning',
  DEACTIVATED: 'error',
};

const AdminClients: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [clientToDeactivate, setClientToDeactivate] = useState<Client | null>(null);
  const [msgConfig, setMsgConfig] = useState({
    type: '',
    message: '',
    snackFlag: false,
  });

  const loadClients = () => {
    setLoading(true);
    fetchClients()
      .then((data) => {
        setClients(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setMsgConfig({
          type: 'error',
          message: 'Failed to load clients.',
          snackFlag: true,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadClients();
  }, []);

  const handleCreate = () => {
    setEditingClient(null);
    setFormOpen(true);
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setFormOpen(true);
  };

  const handleDeactivateClick = (client: Client) => {
    setClientToDeactivate(client);
    setConfirmOpen(true);
  };

  const handleDeactivateConfirm = () => {
    if (!clientToDeactivate) return;
    deactivateClient(clientToDeactivate.id)
      .then(() => {
        setMsgConfig({
          type: 'success',
          message: 'Client deactivated successfully.',
          snackFlag: true,
        });
        loadClients();
      })
      .catch(() => {
        setMsgConfig({
          type: 'error',
          message: 'Failed to deactivate client.',
          snackFlag: true,
        });
      })
      .finally(() => {
        setConfirmOpen(false);
        setClientToDeactivate(null);
      });
  };

  const handleFormSuccess = (message: string) => {
    setMsgConfig({
      type: 'success',
      message,
      snackFlag: true,
    });
    loadClients();
  };

  const handleFormError = (message: string) => {
    setMsgConfig({
      type: 'error',
      message,
      snackFlag: true,
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'organizationName',
      headerName: 'Organization',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'contactEmail',
      headerName: 'Contact Email',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'tier',
      headerName: 'Tier',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={tierColorMap[params.value] || 'default'}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={statusColorMap[params.value] || 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEdit(params.row)}
              aria-label="edit client"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.status !== 'DEACTIVATED' && (
            <Tooltip title="Deactivate">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDeactivateClick(params.row)}
                aria-label="deactivate client"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box className="admin-clients">
      <Box className="admin-clients__header">
        <Typography variant="h5">Client Provisioning</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Add Client
        </Button>
      </Box>

      {loading ? (
        <Box className="admin-clients__loading">
          <CircularProgress />
        </Box>
      ) : (
        <Box className="admin-clients__grid">
          <DataGrid
            rows={clients}
            columns={columns}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            disableRowSelectionOnClick
            autoHeight
          />
        </Box>
      )}

      <ClientFormDialog
        open={formOpen}
        client={editingClient}
        onClose={() => setFormOpen(false)}
        onSuccess={handleFormSuccess}
        onError={handleFormError}
      />

      <ConfirmDialog
        open={confirmOpen}
        title="Deactivate Client"
        message={`Are you sure you want to deactivate "${clientToDeactivate?.organizationName}"? This action will soft-delete the client.`}
        onConfirm={handleDeactivateConfirm}
        onCancel={() => {
          setConfirmOpen(false);
          setClientToDeactivate(null);
        }}
      />

      {msgConfig.snackFlag && <SnackBarMessage snacBarValue={msgConfig} />}
    </Box>
  );
};

export default AdminClients;
